import { TypeOrNull } from "./../../../model/index";
import {
  doEmployeeAssessmentReportFetchApiCall,
  doEmployeeAssessmentsReportFetchApiCall,
  doEmployeeDevelopmentPlanCompletionApiCall,
  doEmployeeDevelopmentPlanCreationApiCall,
  doEmployeeDevelopmentPlanDeletionApiCall,
  doEmployeeDevelopmentPlanItemDeletionApiCall,
  doEmployeeDevelopmentPlanItemEditionApiCall,
} from "./../../../services/company/people";
import {
  DevelopmentPlanItem,
  DevelopmentPlanItemResponse,
  DevelopmentPlanQueryParams,
  EmployeeAssessmentReport,
  EmployeeAssessmentsReport,
} from "./../../../model/people";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { employeesFetchStart, employeesFetchSuccess } from "./employee.store";
import {
  doEmployeesFetchApiCall,
  doEmployeeCreationApiCall,
  doEmployeeFetchApiCall,
  doEmployeeEditionApiCall,
} from "../../../services/company/employee";
import { doCompanyProfileUpdateApiCall } from "../../../services/auth/company";
import { Employee } from "../../../model/user";
import { getActionErrorMessage, getCompanyId } from "../../../shared/utilities";
import { ApiBaseError } from "../../../model/error";
import {
  DevelopmentPlan,
  DevelopmentPlanTemplate,
  PeopleListEmployee,
} from "../../../model/people";
import {
  doDevelopmentPlanTemplateFetchApiCall,
  doEmployeeDevelopmentPlanItemCreationApiCall,
  doEmployeeDevelopmentPlansFetchApiCall,
  doPeopleFetchApiCall,
} from "../../../services/company/people";

export const doEmployeeFetch = createAsyncThunk<Employee, { id: number }>(
  "company/employees/fetchEmployee",
  async (payload, { rejectWithValue }) => {
    try {
      const { id } = payload;
      const companyId = getCompanyId();

      const response = await doEmployeeFetchApiCall({
        companyId,
        id,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching employee")
      );
    }
  }
);

export const doPeopleEmployeesFetch = createAsyncThunk<
  PeopleListEmployee[],
  { departmentId: number; params?: { q?: TypeOrNull<string> } }
>("company/people/fetchEmployees", async (payload, { rejectWithValue }) => {
  try {
    const { departmentId, params } = payload;
    const companyId = getCompanyId();

    const response = await doPeopleFetchApiCall({
      companyId,
      departmentId,
      params,
    });

    return response.data;
  } catch (err) {
    const error = err as ApiBaseError;
    return rejectWithValue(
      getActionErrorMessage(error, "Error fetching People list")
    );
  }
});

export const doDevelopmentPlanTemplateFetch = createAsyncThunk<
  DevelopmentPlanTemplate,
  { templateId?: number }
>(
  "company/people/developmentPlanTemplate",
  async (payload, { rejectWithValue }) => {
    try {
      const { templateId } = payload;
      const companyId = getCompanyId();

      const response = await doDevelopmentPlanTemplateFetchApiCall({
        companyId,
        templateId,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching Development Plan template")
      );
    }
  }
);

export const doEmployeeDevelopmentPlansFetch = createAsyncThunk<
  DevelopmentPlan[],
  { employeeId: number; queryParams?: DevelopmentPlanQueryParams }
>(
  "company/people/employee/developmentPlans",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId, queryParams } = payload;

      const companyId = getCompanyId();
      const response = await doEmployeeDevelopmentPlansFetchApiCall({
        companyId,
        employeeId,
        params: queryParams,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching Development Plans")
      );
    }
  }
);

export const doEmployeeDevelopmentPlanCreation = createAsyncThunk<
  DevelopmentPlan,
  { employeeId: number }
>(
  "company/people/employee/developmentPlan/creation",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId } = payload;

      const companyId = getCompanyId();
      const response = await doEmployeeDevelopmentPlanCreationApiCall({
        companyId,
        employeeId,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(
          error,
          "Error marking Development Plan as completed"
        )
      );
    }
  }
);

export const doEmployeeDevelopmentPlanDeletion = createAsyncThunk<
  DevelopmentPlan,
  { employeeId: number; developmentPlanId: number }
>(
  "company/people/employee/developmentPlan/creation",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId, developmentPlanId } = payload;

      const companyId = getCompanyId();
      const response = await doEmployeeDevelopmentPlanDeletionApiCall({
        companyId,
        employeeId,
        developmentPlanId,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(
          error,
          "Error marking Development Plan as completed"
        )
      );
    }
  }
);

export const doEmployeeDevelopmentPlanCompletion = createAsyncThunk<
  DevelopmentPlan,
  { employeeId: number; developmentPlanId: number; revert: boolean }
>(
  "company/people/employee/developmentPlan/completion",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId, developmentPlanId, revert } = payload;

      const companyId = getCompanyId();
      const response = await doEmployeeDevelopmentPlanCompletionApiCall({
        companyId,
        employeeId,
        developmentPlanId,
        revert,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(
          error,
          "Error marking Development Plan as completed"
        )
      );
    }
  }
);

export const doEmployeeDevelopmentPlanItemCreation = createAsyncThunk<
  DevelopmentPlanItemResponse,
  {
    employeeId: number;
    developmentPlanId: number;
    developmentPlanTemplateItemId: number;
    value: string;
  }
>(
  "company/people/employee/developmentPlan/itemCreation",
  async (payload, { rejectWithValue }) => {
    try {
      const {
        employeeId,
        developmentPlanId,
        developmentPlanTemplateItemId,
        value,
      } = payload;
      const companyId = getCompanyId();

      const response = await doEmployeeDevelopmentPlanItemCreationApiCall({
        companyId,
        employeeId,
        developmentPlanId,
        developmentPlanTemplateItemId,
        value,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error creating Development Plan item")
      );
    }
  }
);

export const doEmployeeDevelopmentPlanItemEdition = createAsyncThunk<
  DevelopmentPlanItem,
  {
    employeeId: number;
    developmentPlanId: number;
    itemId: number;
    developmentPlanTemplateItemId: number;
    value: string;
  }
>(
  "company/people/employee/developmentPlan/itemEdition",
  async (payload, { rejectWithValue }) => {
    try {
      const {
        employeeId,
        developmentPlanId,
        itemId,
        developmentPlanTemplateItemId,
        value,
      } = payload;
      const companyId = getCompanyId();

      const response = await doEmployeeDevelopmentPlanItemEditionApiCall({
        companyId,
        employeeId,
        developmentPlanId,
        itemId,
        developmentPlanTemplateItemId,
        value,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error editing Development Plan item")
      );
    }
  }
);

export const doEmployeeDevelopmentPlanItemDeletion = createAsyncThunk<
  DevelopmentPlanItemResponse,
  {
    employeeId: number;
    developmentPlanId: number;
    itemId: number;
  }
>(
  "company/people/employee/developmentPlan/itemEdition",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId, developmentPlanId, itemId } = payload;
      const companyId = getCompanyId();

      const response = await doEmployeeDevelopmentPlanItemDeletionApiCall({
        companyId,
        employeeId,
        developmentPlanId,
        itemId,
      });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error deleting Development Plan item")
      );
    }
  }
);

export const doEmployeeAssessmentsReportFetch = createAsyncThunk<
  EmployeeAssessmentsReport[],
  { employeeId: number }
>(
  "company/employees/fetchEmployees",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const companyId = getCompanyId();
      const { employeeId } = payload;
      const response = await doEmployeeAssessmentsReportFetchApiCall({
        companyId,
        employeeId,
      });
      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching assessments report")
      );
    }
  }
);

export const doEmployeeAssessmentReportFetch = createAsyncThunk<
  EmployeeAssessmentReport,
  { employeeId: number; assessmentId: number }
>(
  "company/employees/fetchEmployees",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const companyId = getCompanyId();
      const { employeeId, assessmentId } = payload;
      const response = await doEmployeeAssessmentReportFetchApiCall({
        companyId,
        employeeId,
        assessmentId,
      });
      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching assessments report")
      );
    }
  }
);
export const doAssessmentEmployeesFetch = createAsyncThunk<Employee[]>(
  "company/employees/fetchEmployees",
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(employeesFetchStart());
    try {
      const companyId = getCompanyId();
      const response = await doEmployeesFetchApiCall({
        companyId,
      });

      dispatch(employeesFetchSuccess(response.data));
      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching employees")
      );
    }
  }
);
export const doCompanyEmployeesParamsFetch = createAsyncThunk<
  Employee[],
  | { params?: { q?: string; departmentId?: number; onlyHeadOfDepts?: number } }
  | undefined
>(
  "company/employees/queryEmployees",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      let params = payload ? payload.params : undefined;
      const companyId = getCompanyId();

      const response = await doEmployeesFetchApiCall({ companyId, params });

      return response.data;
    } catch (err) {
      const error = err as ApiBaseError;
      return rejectWithValue(
        getActionErrorMessage(error, "Error fetching employees")
      );
    }
  }
);
export const doCompanyEmployeeCreation = createAsyncThunk<
  Employee,
  {
    data: {
      name: string;
      email: string;
      departmentId?: number;
    };
  }
>("company/employees/createEmployee", async (payload, { rejectWithValue }) => {
  try {
    const { data } = payload;
    const companyId = getCompanyId();

    const response = await doEmployeeCreationApiCall({ companyId, data });

    return response.data;
  } catch (err) {
    const error = err as ApiBaseError;
    return rejectWithValue(
      getActionErrorMessage(error, "Error creating employee")
    );
  }
});
export const doCompanyEmployeeEdition = createAsyncThunk<
  Employee,
  {
    employeeId: number;
    data: {
      name?: string;
      companyRole?: string;
      departmentId?: number;
    };
  }
>("company/employees/createEmployee", async (payload, { rejectWithValue }) => {
  try {
    const { employeeId, data } = payload;
    const companyId = getCompanyId();

    const response = await doEmployeeEditionApiCall({
      companyId,
      employeeId,
      data,
    });

    return response.data;
  } catch (err) {
    const error = err as ApiBaseError;
    return rejectWithValue(
      getActionErrorMessage(error, "Error creating employee")
    );
  }
});
export const doCompanyEmployeeProfileUpdate = createAsyncThunk<
  Employee,
  {
    data: {
      name?: string;
      companyRole?: string;
      isHeadOfDepartment?: boolean;
      managerEmployeeId?: number;
    };
  }
>("company/employees/updateEmployee", async (payload, { rejectWithValue }) => {
  try {
    const { data } = payload;

    const response = await doCompanyProfileUpdateApiCall({ data });

    return response.data;
  } catch (err) {
    const error = err as ApiBaseError;
    return rejectWithValue(
      getActionErrorMessage(error, "Error updating employee profile")
    );
  }
});
